// Shared helpers for rendering listing evidence excerpts (#1062).
//
// The backend buildability extractor (`scripts/extract_buildability.py`,
// `_snippet`) slices a fixed ~60-char context window on each side of a
// keyword match. Because that window is cut by character offset, excerpts
// routinely begin and end *mid-word* — e.g. "oy territorial views …" or
// "… offering flexibility for residen". Those partial edge tokens read as
// noise in the Evidence panel.
//
// `trimExcerptToWords` cleans a stored excerpt for display by dropping the
// partial leading/trailing token and marking each trimmed edge with an
// ellipsis so it reads as whole words. It operates purely on the excerpt
// text (the original description is not available to the client), using
// three cheap, reliable heuristics:
//
//   * Length gate — only excerpts long enough to be a *mid-string* window
//     (>= MIN_WINDOW_LEN chars, since the extractor pads ~60 chars on each
//     side of the match) are edge-trimmed. A short snippet is either the
//     whole description or already lands on real boundaries, so it is left
//     untouched.
//   * Leading edge — a real sentence/phrase boundary begins with a capital
//     letter, a digit, or opening punctuation. A long excerpt that starts
//     with a lowercase letter was almost certainly sliced inside a word, so
//     the first token is dropped.
//   * Trailing edge — a long excerpt that does not end on terminal
//     punctuation (. ! ? …) was sliced inside a word, so the last token is
//     dropped.
//
// At most one token is removed per edge, so a genuinely whole excerpt loses
// nothing meaningful and the result always stays readable.

const ELLIPSIS = '…';

// Excerpts shorter than this are not windowed mid-string cuts (the extractor
// window is ~60 chars of context on each side of the match, so real windowed
// excerpts run well over 100 chars). Below the gate we render as-is.
const MIN_WINDOW_LEN = 90;

// Trailing chars that indicate a natural stopping point (sentence end or a
// closing bracket/quote that follows one). If the excerpt ends on any of
// these we leave the final token intact.
const TERMINAL_PUNCT = /[.!?…)"'\]]$/;

/**
 * Trim a windowed evidence excerpt so it begins and ends on whole words.
 *
 * @param {string} text - raw excerpt as stored by the extractor.
 * @returns {string} the cleaned excerpt with ellipses on trimmed edges.
 */
export function trimExcerptToWords(text) {
  if (text == null) return '';
  // Collapse internal whitespace and strip any ellipsis the extractor may
  // already have appended (>200-char windows) so we control edge markers.
  let s = String(text).replace(/\s+/g, ' ').trim();
  s = s.replace(/^…+/, '').replace(/…+$/, '').trim();
  if (!s) return '';

  // Short excerpts are not mid-string windows — render them verbatim.
  if (s.length < MIN_WINDOW_LEN) return s;

  let cutStart = false;
  let cutEnd = false;

  // --- Leading partial word ---
  if (/^[a-z]/.test(s)) {
    const sp = s.indexOf(' ');
    if (sp > 0) {
      const rest = s.slice(sp + 1).trim();
      if (rest) {
        s = rest;
        cutStart = true;
      }
    }
  }

  // --- Trailing partial word ---
  if (!TERMINAL_PUNCT.test(s)) {
    const sp = s.lastIndexOf(' ');
    if (sp > 0) {
      const head = s.slice(0, sp).trim();
      if (head) {
        s = head;
        cutEnd = true;
      }
    }
  }

  // Drop punctuation left dangling by the trailing cut (e.g. a comma that
  // preceded the removed token).
  if (cutEnd) {
    s = s.replace(/[,:;–—-]+$/, '').trim();
  }
  if (!s) return '';

  return `${cutStart ? ELLIPSIS : ''}${s}${cutEnd ? ELLIPSIS : ''}`;
}

export default trimExcerptToWords;
